import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ProcessSelectors, StateWithProcess, UserIdService } from '@spartacus/core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { ADD_VOUCHER_PROCESS_ID, CartActions, StateWithCustomCart, VoucherErrorSelectors } from '../store';
import { ActiveCartService } from './active-cart.service';

@Injectable({
  providedIn: 'root',
})
export class CartVoucherService {
  constructor(
    protected store: Store<StateWithProcess<void> & StateWithCustomCart>,
    protected activeCartService: ActiveCartService,
    protected userIdService: UserIdService,
  ) {}

  addVoucher(voucherId: string, cartId?: string): void {
    this.combineUserAndCartId(cartId).subscribe(([occUserId, cartIdentifier]) =>
      this.store.dispatch(
        new CartActions.CartAddVoucher({
          userId: occUserId,
          cartId: cartIdentifier,
          voucherId: voucherId,
        }),
      ),
    );
  }

  addVoucherToActiveCart(voucherId: string): void {
    this.activeCartService
      .requireLoadedCart()
      .pipe(
        switchMap(() => this.activeCartService.getActiveCartId()),
        filter((cartId: string) => !!cartId),
        take(1),
      )
      .subscribe((cartId: string) => {
        this.addVoucher(voucherId, cartId);
      });
  }

  removeVoucher(voucherId: string, cartId?: string): void {
    this.combineUserAndCartId(cartId).subscribe(([occUserId, cartIdentifier]) =>
      this.store.dispatch(
        new CartActions.CartRemoveVoucher({
          userId: occUserId,
          cartId: cartIdentifier,
          voucherId: voucherId,
        }),
      ),
    );
  }

  /**
   * Get add voucher process error flag
   */
  getAddVoucherResultError(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessErrorFactory(ADD_VOUCHER_PROCESS_ID)));
  }

  /**
   * Get add voucher process success flag
   */
  getAddVoucherResultSuccess(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessSuccessFactory(ADD_VOUCHER_PROCESS_ID)));
  }

  /**
   * Get add voucher process loading flag
   */
  getAddVoucherResultLoading(): Observable<boolean> {
    return this.store.pipe(select(ProcessSelectors.getProcessLoadingFactory(ADD_VOUCHER_PROCESS_ID)));
  }

  /**
   * Get error message after process add voucher error
   */
  getAddVoucherError(): Observable<any | null> {
    return this.store.pipe(select(VoucherErrorSelectors.getVoucherError));
  }

  /**
   * Reset add voucher process
   */
  resetAddVoucherProcessingState(): void {
    this.store.dispatch(new CartActions.CartResetAddVoucher());
  }

  private combineUserAndCartId(cartId: string): Observable<[string, string]> {
    if (cartId) {
      return this.userIdService.getUserId().pipe(
        take(1),
        map((userId) => [userId, cartId]),
      );
    } else {
      return combineLatest([this.userIdService.getUserId(), this.activeCartService.getActiveCartId()]).pipe(take(1));
    }
  }
}
